import type { DeepReadonly } from "@itandi/itandi-bb-ui";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { Input } from "@itandi/itandi-bb-ui/Input";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { useId } from "react";
import { useForm } from "react-hook-form";
import { useSWRConfig } from "swr";
import type {
	LineUserAccount,
	LineUserAccountEdit
} from "~/models/LineUserAccount";
import { buildInitialLineUserAccount } from "~/models/LineUserAccount";
import { isSuccess } from "~/utils/api/api_handler";
import { create, update } from "~/utils/api/vendors/LineUserAccounts";
import { generatePath } from "~/utils/generatePath";

type Props = DeepReadonly<{
	handleClose: () => void;
	lineUserAccount?: LineUserAccount | null;
}>;

export function LineUserAccountModal({
	handleClose,
	lineUserAccount = null
}: Props) {
	const { notify } = useNotification();
	const formId = useId();
	const { mutate: globalMutate } = useSWRConfig();
	const methods = useForm<LineUserAccountEdit>({
		defaultValues: lineUserAccount ?? buildInitialLineUserAccount(),
		mode: "onBlur"
	});
	const { errors, isDirty, isSubmitting } = methods.formState;
	const newRecord = lineUserAccount === null;

	const onSubmit = async (values: LineUserAccountEdit): Promise<void> => {
		const result = newRecord
			? await create(values)
			: await update(values, lineUserAccount.id);
		await globalMutate(generatePath("/vendors/line_user_accounts"));
		if (isSuccess(result)) {
			notify({
				content: newRecord ? "追加しました。" : "更新しました。",
				variant: "success"
			});
			handleClose();
		} else {
			notify({
				content: `エラーが発生しました。${result.message}`,
				variant: "error"
			});
		}
	};

	return (
		<Modalv2 isOpen onClose={handleClose} size="s">
			<ModalHeader title="LINEユーザーアカウント設定" />
			<ModalBody>
				<form id={formId} noValidate onSubmit={methods.handleSubmit(onSubmit)}>
					<FormGroup variant="vertical">
						{newRecord ? (
							<FormControl isInvalid={!!errors.token} label="認証コード">
								<Input
									{...methods.register("token", {
										required: "入力してください"
									})}
								/>
								<FormControlText error={errors.token?.message} />
							</FormControl>
						) : null}
						<FormControl isInvalid={!!errors.name} label="送信先の名前">
							<Input
								{...methods.register("name", {
									required: "入力してください"
								})}
							/>
							<FormControlText error={errors.name?.message} />
						</FormControl>
					</FormGroup>
				</form>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Button
						disabled={isSubmitting}
						onClick={handleClose}
						type="button"
						variant="secondary"
					>
						キャンセル
					</Button>
				}
				rightArea={
					<Button
						disabled={!isDirty}
						form={formId}
						loading={isSubmitting}
						type="submit"
						variant="primary"
					>
						{newRecord ? "追加" : "更新"}
					</Button>
				}
			/>
		</Modalv2>
	);
}
